import { Schema } from 'effect';
import { LaunchpadIdSchema } from './launchpad';
import { MissionIdSchema } from './mission';
import { VehicleIdSchema } from './vehicle';

export const WeatherConditionsSchema = Schema.transform(
  Schema.Struct({
    wind_speed_ms: Schema.optional(Schema.NullOr(Schema.Number)),
    wind_gust_speed_ms: Schema.optional(Schema.NullOr(Schema.Number)),
    precipitation_mm_hr: Schema.optional(Schema.NullOr(Schema.Number)),
    precipitation_probability_pct: Schema.optional(Schema.NullOr(Schema.Number)),
    temperature_deg_c: Schema.optional(Schema.NullOr(Schema.Number))
  }),
  Schema.Struct({
    windSpeedMs: Schema.optional(Schema.NullOr(Schema.Number)),
    windGustSpeedMs: Schema.optional(Schema.NullOr(Schema.Number)),
    precipitationMmHr: Schema.optional(Schema.NullOr(Schema.Number)),
    precipitationProbabilityPct: Schema.optional(Schema.NullOr(Schema.Number)),
    temperatureDegC: Schema.optional(Schema.NullOr(Schema.Number))
  }),
  {
    strict: true,
    decode: (api) => ({
      windSpeedMs: api.wind_speed_ms,
      windGustSpeedMs: api.wind_gust_speed_ms,
      precipitationMmHr: api.precipitation_mm_hr,
      precipitationProbabilityPct: api.precipitation_probability_pct,
      temperatureDegC: api.temperature_deg_c
    }),
    encode: (client) => ({
      wind_speed_ms: client.windSpeedMs,
      wind_gust_speed_ms: client.windGustSpeedMs,
      precipitation_mm_hr: client.precipitationMmHr,
      precipitation_probability_pct: client.precipitationProbabilityPct,
      temperature_deg_c: client.temperatureDegC
    })
  }
);
export type WeatherConditions = Schema.Schema.Type<typeof WeatherConditionsSchema>;

export const WeatherInfoSchema = Schema.transform(
  Schema.Struct({
    weather_conditions: Schema.optional(Schema.NullOr(Schema.encodedSchema(WeatherConditionsSchema))),
    error_text: Schema.optional(Schema.NullOr(Schema.String))
  }),
  Schema.Struct({
    weatherConditions: Schema.optional(Schema.NullOr(WeatherConditionsSchema)),
    errorText: Schema.optional(Schema.NullOr(Schema.String))
  }),
  {
    strict: true,
    decode: (api) => ({
      weatherConditions: api.weather_conditions,
      errorText: api.error_text
    }),
    encode: (client) => ({
      weather_conditions: client.weatherConditions,
      error_text: client.errorText
    })
  }
);
export type WeatherInfo = Schema.Schema.Type<typeof WeatherInfoSchema>;

export const BatteryConsumptionEstimationSchema = Schema.transform(
  Schema.Struct({
    distance_consumption_pct: Schema.Number,
    waypoint_count: Schema.Number,
    delivery_point_count: Schema.Number,
    waypoint_consumption_pct: Schema.Number,
    dropoff_consumption_pct: Schema.Number,
    total_consumption_pct: Schema.Number,
    is_sufficient: Schema.Boolean,
    flight_distance_m: Schema.Number
  }),
  Schema.Struct({
    distanceConsumptionPct: Schema.Number,
    waypointCount: Schema.Number,
    deliveryPointCount: Schema.Number,
    waypointConsumptionPct: Schema.Number,
    dropoffConsumptionPct: Schema.Number,
    totalConsumptionPct: Schema.Number,
    isSufficient: Schema.Boolean,
    flightDistanceM: Schema.Number
  }),
  {
    strict: true,
    decode: (api) => ({
      distanceConsumptionPct: api.distance_consumption_pct,
      waypointCount: api.waypoint_count,
      deliveryPointCount: api.delivery_point_count,
      waypointConsumptionPct: api.waypoint_consumption_pct,
      dropoffConsumptionPct: api.dropoff_consumption_pct,
      totalConsumptionPct: api.total_consumption_pct,
      isSufficient: api.is_sufficient,
      flightDistanceM: api.flight_distance_m
    }),
    encode: (client) => ({
      distance_consumption_pct: client.distanceConsumptionPct,
      waypoint_count: client.waypointCount,
      delivery_point_count: client.deliveryPointCount,
      waypoint_consumption_pct: client.waypointConsumptionPct,
      dropoff_consumption_pct: client.dropoffConsumptionPct,
      total_consumption_pct: client.totalConsumptionPct,
      is_sufficient: client.isSufficient,
      flight_distance_m: client.flightDistanceM
    })
  }
);
export type BatteryConsumptionEstimation = Schema.Schema.Type<typeof BatteryConsumptionEstimationSchema>;

export const PreflightOverviewNotificationSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema,
    weather_information: Schema.optional(Schema.NullOr(Schema.encodedSchema(WeatherInfoSchema))),
    battery_consumption_estimation: Schema.encodedSchema(BatteryConsumptionEstimationSchema)
  }),
  Schema.Struct({
    missionId: MissionIdSchema,
    weatherInformation: Schema.optional(Schema.NullOr(WeatherInfoSchema)),
    batteryConsumptionEstimation: BatteryConsumptionEstimationSchema
  }),
  {
    strict: true,
    decode: (api) => ({
      missionId: api.mission_id,
      weatherInformation: api.weather_information,
      batteryConsumptionEstimation: api.battery_consumption_estimation
    }),
    encode: (client) => ({
      mission_id: client.missionId,
      weather_information: client.weatherInformation,
      battery_consumption_estimation: client.batteryConsumptionEstimation
    })
  }
);
export type PreflightOverviewNotification = Schema.Schema.Type<typeof PreflightOverviewNotificationSchema>;

export const PreflightInfoSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema,
    vehicle_id: VehicleIdSchema,
    vehicle_name: Schema.String,
    started: Schema.Boolean
  }),
  Schema.Struct({
    missionId: MissionIdSchema,
    vehicleId: VehicleIdSchema,
    vehicleName: Schema.String,
    started: Schema.Boolean
  }),
  {
    strict: true,
    decode: (api) => ({
      missionId: api.mission_id,
      vehicleId: api.vehicle_id,
      vehicleName: api.vehicle_name,
      started: api.started
    }),
    encode: (client) => ({
      mission_id: client.missionId,
      vehicle_id: client.vehicleId,
      vehicle_name: client.vehicleName,
      started: client.started
    })
  }
);
export type PreflightInfo = Schema.Schema.Type<typeof PreflightInfoSchema>;

export const PreflightMissionAtLaunchpadNotificationSchema = Schema.transform(
  Schema.Struct({
    launchpad_id: LaunchpadIdSchema,
    preflight_info: Schema.optional(Schema.NullOr(Schema.encodedSchema(PreflightInfoSchema)))
  }),
  Schema.Struct({
    launchpadId: LaunchpadIdSchema,
    preflightInfo: Schema.optional(Schema.NullOr(PreflightInfoSchema))
  }),
  {
    strict: true,
    decode: (api) => ({
      launchpadId: api.launchpad_id,
      preflightInfo: api.preflight_info
    }),
    encode: (client) => ({
      launchpad_id: client.launchpadId,
      preflight_info: client.preflightInfo
    })
  }
);
export type PreflightMissionAtLaunchpadNotification = Schema.Schema.Type<
  typeof PreflightMissionAtLaunchpadNotificationSchema
>;

export const CancelPreflightRequestSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema,
    requeue_package: Schema.Boolean
  }),
  Schema.Struct({
    missionId: MissionIdSchema,
    requeuePackage: Schema.Boolean
  }),
  {
    strict: true,
    decode: (api) => ({
      missionId: api.mission_id,
      requeuePackage: api.requeue_package
    }),
    encode: (client) => ({
      mission_id: client.missionId,
      requeue_package: client.requeuePackage
    })
  }
);
export type CancelPreflightRequest = Schema.Schema.Type<typeof CancelPreflightRequestSchema>;
